/**
 * Geometry utilities for line intersection and spatial calculations
 */

/**
 * Check if two line segments intersect using pure math (no external dependencies)
 * Based on: https://stackoverflow.com/questions/9043805/test-if-two-lines-intersect-javascript-function
 *
 * @param p1x - X coordinate of segment 1 start point
 * @param p1y - Y coordinate of segment 1 start point
 * @param p2x - X coordinate of segment 1 end point
 * @param p2y - Y coordinate of segment 1 end point
 * @param p3x - X coordinate of segment 2 start point
 * @param p3y - Y coordinate of segment 2 start point
 * @param p4x - X coordinate of segment 2 end point
 * @param p4y - Y coordinate of segment 2 end point
 * @returns true if the segments intersect, false otherwise
 */
export function segmentsIntersect(
  p1x: number,
  p1y: number,
  p2x: number,
  p2y: number,
  p3x: number,
  p3y: number,
  p4x: number,
  p4y: number
): boolean {
  return (
    segmentIntersectionFraction(p1x, p1y, p2x, p2y, p3x, p3y, p4x, p4y) !== null
  )
}

/**
 * Where along segment 1 (0 at p1, 1 at p2) it crosses segment 2, or null
 * when the segments do not cross.
 */
export function segmentIntersectionFraction(
  p1x: number,
  p1y: number,
  p2x: number,
  p2y: number,
  p3x: number,
  p3y: number,
  p4x: number,
  p4y: number
): number | null {
  const det = (p2x - p1x) * (p4y - p3y) - (p4x - p3x) * (p2y - p1y)

  if (det === 0) {
    return null
  }

  const lambda = ((p4y - p3y) * (p4x - p1x) + (p3x - p4x) * (p4y - p1y)) / det
  const gamma = ((p1y - p2y) * (p4x - p1x) + (p2x - p1x) * (p4y - p1y)) / det

  return lambda > 0 && lambda < 1 && gamma > 0 && gamma < 1 ? lambda : null
}
